import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { DealerUsersProps } from "store/modules/DealerUsers/types.store";
import { useAppDispatch } from "store/hooks";
import { getCoverageAreaDealerId } from "store/modules/Logistics/logisticsCoverageArea.helpers";
import {
  CoverageAreaDriverEntry,
  CoverageAreaProps,
} from "store/modules/Logistics/types.store";
import { patchCoverageAreaDrivers } from "store/modules/Logistics/index.store";
import { handleSaveMapAreaDriversRequest } from "store/modules/Logistics/thunk.store";
import { buildMapAreaDriversStatePatch } from "components/CardDriverArea/buildCoverageAreaDriversStatePatch";
import {
  dealerUsersToDriverOptions,
  getOrderedCoverageAreaDrivers,
  MAX_DRIVERS_PER_MAP_COVERAGE_AREA,
} from "components/CardDriverArea/coverageAreaDrivers";
import { useMapAreaDriverSelection } from "./useMapAreaDriverSelection";
import { mapAreaViewEditButtonSx, selectDeliveryEditButtonSx } from "./muiSx";

interface UseCardDriverAreaMapParams {
  dealerSelected: string;
  currentCoverageArea: CoverageAreaProps;
  dealerDrivers: DealerUsersProps[];
}

export function useCardDriverAreaMap({
  dealerSelected,
  currentCoverageArea,
  dealerDrivers,
}: UseCardDriverAreaMapParams) {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const [allUsers, setAllUsers] = useState<DealerUsersProps[]>(dealerDrivers);
  const [isEditing, setIsEditing] = useState(false);
  const [isCancelModalOpen, setIsCancelModalOpen] = useState(false);
  const [isSaveModalOpen, setIsSaveModalOpen] = useState(false);

  const {
    slotRows,
    resetToDrivers,
    addEmptySlot,
    setSlotValue,
    removeSlot,
    selectedDriversInOrder,
    canAddSlot,
  } = useMapAreaDriverSelection(MAX_DRIVERS_PER_MAP_COVERAGE_AREA);

  const driverOptions = useMemo(
    () => dealerUsersToDriverOptions(allUsers),
    [allUsers],
  );

  const orderedDrivers = useMemo<CoverageAreaDriverEntry[]>(
    () => getOrderedCoverageAreaDrivers(currentCoverageArea),
    [currentCoverageArea],
  );

  const dealerId = getCoverageAreaDealerId(currentCoverageArea);

  useEffect(() => {
    setAllUsers(dealerDrivers);
  }, [dealerDrivers]);

  useEffect(() => {
    setIsEditing(false);
    setIsCancelModalOpen(false);
    setIsSaveModalOpen(false);
  }, [dealerSelected, currentCoverageArea.id]);

  const openEdit = useCallback(() => {
    resetToDrivers(orderedDrivers, allUsers);
    setIsEditing(true);
  }, [allUsers, orderedDrivers, resetToDrivers]);

  const handleCancelEdit = useCallback(() => {
    resetToDrivers(orderedDrivers, allUsers);
    setIsEditing(false);
  }, [allUsers, orderedDrivers, resetToDrivers]);

  const requestCancelEdit = useCallback(() => {
    setIsCancelModalOpen(true);
  }, []);

  const confirmCancelEdit = useCallback(() => {
    setIsCancelModalOpen(false);
    handleCancelEdit();
  }, [handleCancelEdit]);

  const dismissCancelModal = useCallback(() => {
    setIsCancelModalOpen(false);
  }, []);

  const requestSaveEdit = useCallback(() => {
    setIsSaveModalOpen(true);
  }, []);

  const dismissSaveModal = useCallback(() => {
    setIsSaveModalOpen(false);
  }, []);

  const handleSaveMapAreaDrivers = useCallback(async () => {
    const cappedDrivers = selectedDriversInOrder.slice(
      0,
      MAX_DRIVERS_PER_MAP_COVERAGE_AREA,
    );
    const patch = buildMapAreaDriversStatePatch(
      cappedDrivers,
      dealerId,
      allUsers,
    );
    await dispatch(
      handleSaveMapAreaDriversRequest({
        dealerId,
        coverageAreaId: currentCoverageArea.id,
        selectedDriverIdsInOrder: cappedDrivers.map((driver) => driver.id),
      }),
    ).unwrap();
    dispatch(
      patchCoverageAreaDrivers({
        coverageAreaId: currentCoverageArea.id,
        patch,
      }),
    );
    setIsCancelModalOpen(false);
    setIsSaveModalOpen(false);
    setIsEditing(false);
  }, [
    allUsers,
    currentCoverageArea.id,
    dealerId,
    dispatch,
    selectedDriversInOrder,
  ]);

  const confirmSaveEdit = useCallback(async () => {
    setIsSaveModalOpen(false);
    await handleSaveMapAreaDrivers().catch(() => {});
  }, [handleSaveMapAreaDrivers]);

  const hasDrivers = orderedDrivers.length > 0;
  const hasMultipleDrivers = orderedDrivers.length > 1;

  const primaryActionLabel = hasDrivers
    ? t("editDeliveryPeople")
    : t("selectDeliveryPeople");

  const sectionTitle = hasDrivers
    ? t("defineDeliveryPeopleSectionTitle")
    : t("selectDeliveryPeopleSectionTitle");

  const editButtonSx = hasMultipleDrivers
    ? mapAreaViewEditButtonSx
    : selectDeliveryEditButtonSx;

  return {
    isEditing,
    slotRows,
    driverOptions,
    dealerDeliveryUsers: allUsers,
    orderedDrivers,
    canAddSlot,
    hasDrivers,
    hasMultipleDrivers,
    primaryActionLabel,
    sectionTitle,
    editButtonSx,
    openEdit,
    requestSaveEdit,
    addEmptySlot,
    setSlotValue,
    removeSlot,
    isCancelModalOpen,
    requestCancelEdit,
    confirmCancelEdit,
    dismissCancelModal,
    isSaveModalOpen,
    confirmSaveEdit,
    dismissSaveModal,
  };
}
